// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useStepsData } from '@shared/ui-kit';
import { formatMoneyRange, formatToMoney } from '@shared/ui-kit/utils';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import type { WealthSourceFundsFormData } from '@shared/advisory/account-workflow/types';
import { wealthSourceFundsSchema } from '@shared/advisory/account-workflow/types';
import { produce } from 'immer';

export const useWealthSourceFunds = () => {
  const { t } = useTranslation();

  const currency = useSelector(selectAccountCurrency);

  const { next } = useStepsData();

  const { getFormData, saveFormData, markStepAsCompleted, isStepCompleted } =
    useAccountWorkflowData();

  const savedData = getFormData('wealthSourceFunds');

  const form = useForm<WealthSourceFundsFormData>({
    resolver: zodResolver(wealthSourceFundsSchema),
    mode: 'onChange',
    defaultValues: savedData,
  });

  const wealthSourceFunds = useWatch({
    control: form.control,
  });

  const estimatedAnnualIncomeOptions = useMemo(() => {
    const amount = (value: number) => formatToMoney(value, currency);

    return [
      {
        value: 'annual-under-50k',
        label: `< ${amount(50_000)}`,
      },
      {
        value: 'annual-50k-100k',
        label: formatMoneyRange(50_000, 100_000, currency),
      },
      {
        value: 'annual-over-100k',
        label: `> ${amount(100_000)}`,
      },
    ];
  }, [currency]);

  const sourceOfFundsOptions = useMemo(
    () => [
      {
        value: 'funds-savings',
        label: t(
          'account-opening.wealth-source-funds.source-of-funds.options.savings',
          { ns: 'components' },
        ),
      },
      {
        value: 'funds-salary',
        label: t(
          'account-opening.wealth-source-funds.source-of-funds.options.salary-fees',
          { ns: 'components' },
        ),
      },
      {
        value: 'funds-asset-sale',
        label: t(
          'account-opening.wealth-source-funds.source-of-funds.options.asset-sale',
          { ns: 'components' },
        ),
      },
      {
        value: 'funds-inheritance',
        label: t(
          'account-opening.wealth-source-funds.source-of-funds.options.inheritance-donation',
          { ns: 'components' },
        ),
      },
      {
        value: 'funds-lottery',
        label: t(
          'account-opening.wealth-source-funds.source-of-funds.options.prizes-lottery',
          { ns: 'components' },
        ),
      },
    ],
    [t],
  );

  const mainIncomeSourceOptions = useMemo(
    () => [
      {
        value: 'income-salary-pension',
        label: t(
          'account-opening.wealth-source-funds.income-and-wealth.main-income-source.options.salary-pension',
          { ns: 'components' },
        ),
      },
      {
        value: 'income-fees',
        label: t(
          'account-opening.wealth-source-funds.income-and-wealth.main-income-source.options.fees',
          { ns: 'components' },
        ),
      },
      {
        value: 'income-self-employed',
        label: t(
          'account-opening.wealth-source-funds.income-and-wealth.main-income-source.options.own-business',
          { ns: 'components' },
        ),
      },
      {
        value: 'income-assets',
        label: t(
          'account-opening.wealth-source-funds.income-and-wealth.main-income-source.options.asset-income',
          { ns: 'components' },
        ),
      },
    ],
    [t],
  );

  const approximateWealthOptions = useMemo(() => {
    const amount = (value: number) => formatToMoney(value, currency);

    return [
      {
        value: 'between-1-20k',
        label: formatMoneyRange(1, 20_000, currency),
      },
      {
        value: 'between-20k-50k',
        label: formatMoneyRange(20_001, 49_999, currency),
      },
      {
        value: 'between-50k-100k',
        label: formatMoneyRange(50_000, 100_000, currency),
      },
      {
        value: 'between-100k-300k',
        label: formatMoneyRange(100_001, 300_000, currency),
      },
      {
        value: 'between-300k-600k',
        label: formatMoneyRange(300_001, 600_000, currency),
      },
      {
        value: 'between-600k-1m',
        label: formatMoneyRange(600_001, 999_999, currency),
      },
      {
        value: 'over-1m',
        label: `+ ${amount(1_000_000)}`,
      },
    ];
  }, [currency]);

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    async (data: WealthSourceFundsFormData) => {
      if (isStepCompleted('wealth-source-funds', form.formState.isDirty)) {
        next();

        return;
      }

      saveFormData('wealthSourceFunds', data);

      markStepAsCompleted('wealth-source-funds');

      next();
    },
    [
      form.formState.isDirty,
      markStepAsCompleted,
      next,
      saveFormData,
      isStepCompleted,
    ],
  );

  const onToggleSourceOfFunds = useCallback(
    (value: string) => {
      const currentValue = form.getValues('sourceOfFunds') ?? [];

      const nextValue = produce(currentValue, draft => {
        if (draft.includes(value)) {
          draft.splice(draft.indexOf(value), 1);
        } else {
          draft.push(value);
        }
      });

      form.setValue('sourceOfFunds', nextValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  return {
    t,
    form,
    onNext,
    isFormValid,
    wealthSourceFunds,
    estimatedAnnualIncomeOptions,
    sourceOfFundsOptions,
    mainIncomeSourceOptions,
    approximateWealthOptions,
    onToggleSourceOfFunds,
  };
};
